// Die Sitzung: wer angemeldet ist, worauf er zugreifen darf und wie er zwischen seinen beiden
// Rollen wechselt (spec §1.11). Eine einzige Abfrage — GET /api/auth/me — trägt beides, und der
// Cache-Schlüssel ist überall derselbe, damit nach dem Abmelden nichts stehen bleibt.
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useRef, type ReactElement, type ReactNode } from 'react'
import { api, istApiError, istNichtAngemeldet, type Sitzung } from './api'

export const SITZUNG_SCHLUESSEL = ['sitzung'] as const

// TanStack Router typt `to` gegen den erzeugten Routenbaum; ein Pfad mit Parametern ist von hier
// aus nur als Zeichenkette bekannt. Diese schmale Signatur beschreibt, was gebraucht wird.
type Ziel = { to: string; params?: Record<string, string>; search?: Record<string, unknown>; replace?: boolean }
function useZielNavigation(): (ziel: Ziel) => void {
  return useNavigate() as unknown as (ziel: Ziel) => void
}

export interface WeiterleitungProps {
  nach: string
  params?: Record<string, string>
  search?: Record<string, unknown>
}

/**
 * Eine Umleitung, die genau einmal umleitet.
 *
 * Hier stand `<Navigate>`, und das war ein Fehler mit Folgen: die Komponente vergleicht in ihrem
 * Effekt die aktuellen Props mit den vorigen — per Identität. JSX baut bei jedem Bild ein neues
 * Props-Objekt, der Vergleich schlägt also immer aus, und jedes Bild löst eine neue Navigation aus,
 * die das nächste Bild auslöst. Wer ohne Sitzung eine geschützte Adresse öffnete, bekam damit keine
 * Anmeldung, sondern einen Browser, der sich festfrisst, bis ihm der Speicher ausgeht.
 *
 * Sichtbar wurde das erst, als ein Test die echte Anwendung an einer echten Adresse rendert
 * (test/client/huelle.test.tsx). Jeder frühere Test ersetzte den Router durch ein Mock — und ein
 * Mock von `Navigate` navigiert nicht, es zeichnet nur einen Satz.
 *
 * Das `role="status"` bleibt: für den Sekundenbruchteil bis zur Anmeldung steht dort ein Satz statt
 * einer weißen Fläche.
 */
export function Weiterleitung({ nach, params, search }: WeiterleitungProps): ReactElement {
  const navigate = useZielNavigation()
  const schonGesprungen = useRef(false)
  // Ohne Abhängigkeitsliste, mit Wächter: der Effekt läuft nach jedem Bild an und tut ab dem
  // zweiten nichts. Eine Liste mit `params`/`search` darin wäre genau der Identitätsvergleich,
  // der oben in die Schleife geführt hat.
  useEffect(() => {
    if (schonGesprungen.current) return
    schonGesprungen.current = true
    navigate({ to: nach, params, search, replace: true })
  })
  return <p role="status">Weiterleitung nach {nach} …</p>
}

/**
 * Die angemeldete Person mit ihren Auftraggebern und ihren Auftragnehmer-Profilen.
 * `retry: false`, weil ein 401 keine bessere Antwort bekommt, wenn man ihn wiederholt.
 */
export function useSession(): UseQueryResult<Sitzung, unknown> {
  return useQuery({
    queryKey: SITZUNG_SCHLUESSEL,
    queryFn: () => api.auth.me(),
    retry: false,
    staleTime: 60_000,
  })
}

/**
 * Die geladene Sitzung, ohne Abfragehülle. Nur innerhalb von <RequireSession> aufrufen — dort ist sie
 * da, weil der Zugangsschutz sonst gar nicht bis zu den Kindern kommt.
 *
 * Achtung, die beiden Namen unterscheiden sich um einen Buchstaben und nicht um eine Kleinigkeit:
 * `useSitzung()` (dieses hier) liefert die fertige Sitzung und wirft, wenn keine da ist — das ist
 * der Regelzugang für die Bildschirme der Parts 24–26 (`const { contractors } = useSitzung()`).
 * `useSession()` liefert die Abfragehülle mit Lade- und Fehlerzustand und wird nur dort gebraucht,
 * wo dieser Zustand selbst behandelt wird: <RequireSession> und <MandantenWechsler> hier in dieser
 * Datei und `einladung.tsx`, das ohne Sitzung erreichbar sein muss. Beide Namen stehen so in
 * parts4/00-contract.md und in den Parts 24 und 26 und sind deshalb nicht frei wählbar.
 */
export function useSitzung(): Sitzung {
  const { data } = useSession()
  if (data === undefined) throw new Error('useSitzung außerhalb von <RequireSession> verwendet')
  return data
}

export interface RequireSessionProps {
  children: ReactNode
}

/**
 * Der Zugangsschutz vor jedem Bildschirm. Ohne Sitzung geht es zur Anmeldung — mit dem Ziel im
 * Suchparameter `weiter`, damit der Nutzer nach dem Code dort landet, wo er hinwollte, und nicht
 * auf einer Startseite, die er gar nicht gesucht hat.
 */
export function RequireSession({ children }: RequireSessionProps): ReactElement {
  const sitzung = useSession()
  const ziel = useRouterState({ select: (zustand) => zustand.location.href })

  if (sitzung.isPending) {
    return <p role="status">Sitzung wird geprüft …</p>
  }

  if (sitzung.isError) {
    if (istNichtAngemeldet(sitzung.error)) {
      return <Weiterleitung nach="/login" search={{ weiter: ziel }} />
    }
    // Der Satz kommt wörtlich vom Server; die Oberfläche erfindet für einen Serverfehler keine
    // eigene Formulierung (parts4/00-contract.md).
    return (
      <p role="alert">
        {istApiError(sitzung.error) ? sitzung.error.message : 'Die Sitzung konnte nicht geprüft werden.'}
      </p>
    )
  }

  return <>{children}</>
}

/**
 * Steht diese Sitzung an mehr als einer Stelle? Ein Auftraggeber und ein eigenes
 * Auftragnehmer-Profil sind zwei Bereiche, zwei Auftraggeber ebenso. Die Antwort entscheidet an
 * zwei Orten dasselbe: der Wechsler zeigt sich nur dann (ein Umschalter mit einem Eintrag ist eine
 * Behauptung ohne Inhalt), und die Kopfzeile schreibt den Namen des Bereichs nur dann daneben,
 * wenn der Wechsler ihn nicht ohnehin nennt. Deshalb steht die Regel hier einmal statt zweimal.
 */
export function hatMehrereBereiche(sitzung: Sitzung): boolean {
  return sitzung.tenants.length + (sitzung.contractors.length > 0 ? 1 : 0) >= 2
}

export interface MandantenWechslerProps {
  /** Der Auftraggeber, dessen Bildschirm gerade offen ist; im Auftragnehmer-Bereich `undefined`. */
  aktuellerTenantId?: string
}

/**
 * Der Umschalter im Kopf für alle, die an mehr als einer Stelle stehen — Administrator bei einem
 * Auftraggeber und Auftragnehmer woanders (spec §1.11, letzter Punkt). Gibt es nur einen Bereich,
 * erscheint er nicht: ein Umschalter mit einem Eintrag ist eine Behauptung ohne Inhalt.
 */
export function MandantenWechsler({ aktuellerTenantId }: MandantenWechslerProps): ReactElement | null {
  const sitzung = useSession()
  const navigate = useZielNavigation()

  if (!sitzung.isSuccess) return null

  const { tenants, contractors } = sitzung.data
  if (!hatMehrereBereiche(sitzung.data)) return null

  // Der Wert muss zu einem Eintrag der Liste passen. Nennt die Adresse einen Auftraggeber, den
  // diese Sitzung nicht (mehr) führt — ein alter Link, eine gerade entzogene Mitgliedschaft —,
  // zeigte <select> stillschweigend den ersten Eintrag an und behauptete damit einen Bereich, in
  // dem der Nutzer gar nicht steht; ein Klick auf genau diesen Eintrag löste dann nicht einmal ein
  // Wechsel-Ereignis aus. In dem Fall steht ein deaktivierter Platzhalter davor.
  const bekannt =
    aktuellerTenantId === undefined
      ? contractors.length > 0
      : tenants.some((tenant) => tenant.id === aktuellerTenantId)
  const wert = bekannt ? (aktuellerTenantId ?? 'me') : ''

  return (
    <label>
      <span className="beschriftung">Bereich wechseln</span>
      <select
        value={wert}
        onChange={(ereignis) => {
          const gewaehlt = ereignis.target.value
          if (gewaehlt === 'me') {
            navigate({ to: '/me/provisionen' })
            return
          }
          navigate({ to: '/t/$tenantId/dashboard', params: { tenantId: gewaehlt } })
        }}
      >
        {!bekannt && (
          <option value="" disabled>
            Bitte wählen …
          </option>
        )}
        {tenants.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.name}
          </option>
        ))}
        {contractors.length > 0 && <option value="me">Meine Provisionen</option>}
      </select>
    </label>
  )
}

/**
 * Abmelden. Der Client wirft in jedem Fall seinen gesamten Cache weg und geht zur Anmeldung —
 * `onSettled`, nicht `onSuccess`: auf einem gemeinsam genutzten Rechner dürfen die Zahlen des
 * Vorgängers auch dann nicht stehen bleiben, wenn der Aufruf den Server nicht erreicht hat.
 *
 * Gelingt der Aufruf, löscht der Server dazu die Sitzungszeile und das Cookie. Scheitert er
 * (Netzfehler), bleibt die Sitzung dort bestehen, bis sie abläuft, während der Bildschirm schon
 * die Anmeldung zeigt — der Cache ist dann trotzdem leer, und die nächste Abfrage holt alles neu.
 */
export function useAbmelden(): { abmelden: () => void; laeuft: boolean } {
  const queryClient = useQueryClient()
  const navigate = useZielNavigation()
  const mutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSettled: () => {
      queryClient.clear()
      navigate({ to: '/login' })
    },
  })
  return { abmelden: () => mutation.mutate(), laeuft: mutation.isPending }
}

export function Abmelden(): ReactElement {
  const { abmelden, laeuft } = useAbmelden()
  return (
    <button type="button" onClick={abmelden} disabled={laeuft}>
      Abmelden
    </button>
  )
}
